import React, { useState, useEffect } from 'react';
import { X, Wallet, MapPin, Clock, RefreshCw } from 'lucide-react';
import { getDistributorWalletByIdAPI, type DistributorWalletItem, type WalletTransactionItem } from '../../../api/distributorWallet';

interface WalletDetailDrawerProps {
  distributorId: number | string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const WalletDetailDrawer: React.FC<WalletDetailDrawerProps> = ({
  distributorId,
  isOpen,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [distributor, setDistributor] = useState<DistributorWalletItem | null>(null);
  const [transactions, setTransactions] = useState<WalletTransactionItem[]>([]);

  useEffect(() => {
    if (!isOpen || !distributorId) return;

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getDistributorWalletByIdAPI(distributorId);
        if (res && res.data) {
          setDistributor(res.data);
          if (Array.isArray(res.data.transactions)) {
            setTransactions(res.data.transactions);
          } else {
            setTransactions([]);
          }
        }
      } catch (err: any) {
        console.error('Error fetching distributor wallet details:', err);
        setError(err.message || 'Failed to load distributor wallet details');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [distributorId, isOpen]);

  if (!isOpen) return null;

  const formatCurrency = (val?: number) => {
    if (val === undefined || val === null) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(val);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const currentBal = distributor?.wallet?.currentBalance ?? distributor?.currentBalance ?? distributor?.walletBalance ?? 0;
  const creditLim = distributor?.wallet?.creditLimit ?? distributor?.creditLimit ?? 0;
  const availBal = distributor?.wallet?.totalAvailableBalance ?? distributor?.totalAvailableBalance ?? (currentBal + creditLim);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex justify-end z-50 animate-fadeIn">
      <div className="bg-white w-full max-w-2xl h-full shadow-2xl flex flex-col overflow-hidden animate-slideLeft">
        {/* Drawer Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-rose-100 text-[#B71C1C] flex items-center justify-center font-bold">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">
                {distributor?.companyName || 'Distributor Wallet Details'}
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                SAP Code: <span className="font-bold text-slate-700">{distributor?.sapCode || distributor?.sapCustomerId || distributor?.loginId || '-'}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {loading ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400">
              <RefreshCw className="w-8 h-8 animate-spin text-[#B71C1C]" />
              <span className="text-xs font-semibold text-slate-600">Loading wallet details...</span>
            </div>
          ) : error ? (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-2xl p-4 text-center">
              {error}
            </div>
          ) : (
            <>
              {/* Wallet Summary Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-emerald-50/80 border border-emerald-200/80 rounded-2xl p-4 flex flex-col">
                  <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                    Current Balance
                  </span>
                  <span className="text-lg font-extrabold text-emerald-900 mt-1">
                    {formatCurrency(currentBal)}
                  </span>
                  <span className="text-[10px] text-emerald-600 font-medium mt-0.5">Wallet Funds</span>
                </div>

                <div className="bg-blue-50/80 border border-blue-200/80 rounded-2xl p-4 flex flex-col">
                  <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">
                    Credit Limit
                  </span>
                  <span className="text-lg font-extrabold text-blue-900 mt-1">
                    {formatCurrency(creditLim)}
                  </span>
                  <span className="text-[10px] text-blue-600 font-medium mt-0.5">Approved Line</span>
                </div>

                <div className="bg-purple-50/80 border border-purple-200/80 rounded-2xl p-4 flex flex-col">
                  <span className="text-[11px] font-bold text-purple-700 uppercase tracking-wider">
                    Total Available
                  </span>
                  <span className="text-lg font-extrabold text-purple-900 mt-1">
                    {formatCurrency(availBal)}
                  </span>
                  <span className="text-[10px] text-purple-600 font-medium mt-0.5">Purchasing Power</span>
                </div>
              </div>

              {/* System Identifiers & Profile Summary */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-2">
                  Distributor Identifiers & Profile
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div>
                    <span className="text-slate-500 font-medium">SAP Customer ID:</span>{' '}
                    <span className="font-extrabold text-[#B71C1C]">
                      {distributor?.sapCustomerId || distributor?.sapCode || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Distributor ID:</span>{' '}
                    <span className="font-bold text-slate-800">
                      {distributor?.distributorId || distributor?.id || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Customer ID:</span>{' '}
                    <span className="font-bold text-slate-800">
                      {distributor?.customerId || distributor?.loginId || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Contact Person:</span>{' '}
                    <span className="font-bold text-slate-800">
                      {distributor?.fullName || `${distributor?.firstName || ''} ${distributor?.lastName || ''}`.trim() || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Phone:</span>{' '}
                    <span className="font-bold text-slate-800">{distributor?.phone || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Email:</span>{' '}
                    <span className="font-bold text-slate-800">{distributor?.email || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Payment Terms:</span>{' '}
                    <span className="font-bold text-slate-800">{distributor?.paymentTerms || 'NET30'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">GST No:</span>{' '}
                    <span className="font-bold text-slate-800">{distributor?.gst || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">PAN No:</span>{' '}
                    <span className="font-bold text-slate-800">{distributor?.panNo || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">FSSAI No:</span>{' '}
                    <span className="font-bold text-slate-800">{distributor?.fassiNo || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Location Details (Zone, City, Depo, Route) */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                  <MapPin className="w-3.5 h-3.5 text-[#B71C1C]" />
                  Location Mapping Details
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div>
                    <span className="text-slate-500 font-medium">Zone:</span>{' '}
                    <span className="font-bold text-slate-800">
                      {distributor?.zoneName || distributor?.zone?.name || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">City:</span>{' '}
                    <span className="font-bold text-slate-800">
                      {distributor?.cityName || distributor?.city?.name || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Depo:</span>{' '}
                    <span className="font-bold text-slate-800">
                      {distributor?.depoName || distributor?.depo?.name || distributor?.depoSapName || '-'}
                      {distributor?.depoSapCode ? ` (${distributor.depoSapCode})` : ''}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">Route:</span>{' '}
                    <span className="font-bold text-slate-800">
                      {distributor?.routeName || distributor?.route?.name || '-'}
                      {distributor?.routeSapCode ? ` (${distributor.routeSapCode})` : ''}
                    </span>
                  </div>
                </div>
              </div>

              {/* Address Details */}
              {distributor?.addresses && distributor.addresses.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                    <MapPin className="w-3.5 h-3.5 text-rose-600" />
                    Primary Shipping Address
                  </h4>
                  {distributor.addresses.map((addr, idx) => (
                    <div key={idx} className="text-xs text-slate-700 font-medium leading-relaxed">
                      <p className="font-bold text-slate-800">
                        {addr.addressLine1} {addr.addressLine2 || ''}
                      </p>
                      <p>
                        {addr.cityName ? `${addr.cityName}, ` : ''} Pincode: {addr.pincode || '-'}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Transactions History */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#B71C1C]" />
                    Recent Wallet Transactions ({transactions.length})
                  </h4>
                  {distributor?.wallet?.lastTransactionAt && (
                    <span className="text-[11px] text-slate-400 font-medium">
                      Last: {formatDate(distributor.wallet.lastTransactionAt)}
                    </span>
                  )}
                </div>

                {transactions.length > 0 ? (
                  <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-slate-100 font-bold text-slate-600 border-b border-slate-200">
                        <tr>
                          <th className="px-3.5 py-2.5">Date</th>
                          <th className="px-3.5 py-2.5">Type</th>
                          <th className="px-3.5 py-2.5">Amount</th>
                          <th className="px-3.5 py-2.5">New Balance</th>
                          <th className="px-3.5 py-2.5">Ref / Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                        {transactions.map((tx) => (
                          <tr key={tx.id} className="hover:bg-slate-50">
                            <td className="px-3.5 py-2 text-slate-500 font-medium whitespace-nowrap">
                              {formatDate(tx.createdAt)}
                            </td>
                            <td className="px-3.5 py-2 font-bold">
                              <span
                                className={`px-2 py-0.5 rounded-md text-[10px] uppercase font-extrabold ${
                                  (tx.transactionType || '').toUpperCase().includes('CREDIT')
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-rose-100 text-rose-800'
                                }`}
                              >
                                {tx.transactionType || 'ADJUSTMENT'}
                              </span>
                            </td>
                            <td className="px-3.5 py-2 font-extrabold text-slate-800">
                              {formatCurrency(tx.amount)}
                            </td>
                            <td className="px-3.5 py-2 font-bold text-emerald-700">
                              {formatCurrency(tx.newBalance)}
                            </td>
                            <td className="px-3.5 py-2 text-slate-600">
                              {tx.description || tx.referenceNo || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center text-xs text-slate-400 font-medium">
                    No recent wallet transaction history recorded.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletDetailDrawer;
